<template>
  <PopUp :show="modal" @hideModal="hideModal">
    <ValidationObserver
      v-slot="{ invalid }"
      ref="observer"
      tag="div"
      class="pressrelease"
      id="scroll-block"
    >
      <header class="pressrelease__header">
        <h3 class="pressrelease__header_title">
          {{ pressrelease.id ? "Edit" : "Add" }} Press Release
        </h3>
        <div class="pressrelease__header_actions">
          <BaseButton
            v-if="item"
            class="button--sm button--outline button--uppercase"
            text="Delete"
            @click="$emit('removeItem', pressrelease)"
          ></BaseButton>
          <BaseButton
            class="button--sm button--green button--uppercase"
            text="Save Changes"
            :loading="changesLoading"
            :disabled="invalid || pressrelease.header_image === '' || !changed"
            @click="savePressRelease"
          ></BaseButton>
        </div>
      </header>
      <section class="form">
        <h3 class="form__header">Press Release Information</h3>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col"
            :rules="{ required: true, min: 2, max: 255 }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Title"
              :required="true"
              :validateError="errors[0] || serverError.title"
              v-model="pressrelease.title"
              :element="pressrelease.title"
              @input="pressrelease.title = $event"
            ></BaseInput>
          </ValidationProvider>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true, min: 2, max: 255 }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Author"
              :required="true"
              :validateError="errors[0]"
              v-model="pressrelease.author"
              :element="pressrelease.author"
              @input="pressrelease.author = $event"
            ></BaseInput>
          </ValidationProvider>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true }"
            v-slot="{ errors }"
          >
            <DatePicker
              placeholder="Date Published"
              :time="false"
              :required="true"
              :endDate="today"
              :validateError="errors[0]"
              :value="pressrelease.date_published"
              v-model="pressrelease.date_published"
              @input="pressrelease.date_published = $event"
            ></DatePicker>
          </ValidationProvider>
        </div>
        <div class="form__row form__row--align-start">
          <div class="form__row_col form__row_col--side">
            <BaseSelect
              placeholder="Status"
              type="status"
              v-model="pressrelease.status"
              :selected="pressrelease.status"
              :items="[
                { id: 0, title: 'Draft' },
                { id: 1, title: 'Published' },
              ]"
              @input="pressrelease.status = $event.id"
            ></BaseSelect>
          </div>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ required: true, path: true, max: 255 }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Path"
              :required="true"
              :validateError="errors[0] || serverError.slug"
              v-model="pressrelease.slug"
              :element="pressrelease.slug"
              @input="pressrelease.slug = $event"
            >
              <template #text>
                <span class="form-control__simple-text"
                  >Example: {{ `${url}/press-releases/${pressrelease.slug || "path"}` }}</span
                ></template
              >
            </BaseInput>
          </ValidationProvider>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">Press Release Details</h3>
        <div class="form__row">
          <div class="form__row_col">
            <Editor
              placeholder="Text Editor"
              :value="pressrelease.details"
              @input="pressrelease.details = $event"
              v-model="pressrelease.details"
            ></Editor>
          </div>
        </div>
        <div class="form__row">
          <MultipleAutocompleteInput
            id="autocomplete-tag"
            v-model="pressrelease.tags"
            :focusOpen="true"
            :loading="tagsLoading"
            :items="pressrelease.tags"
            :matches="matches"
            placeholder="Tags"
            @addItem="addTag"
            @input="pressrelease.tags = $event"
            @searchMatches="apiGetMatchedTags"
          ></MultipleAutocompleteInput>
        </div>
        <div class="form__row">
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: 255 }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Button Text"
              :validateError="errors[0]"
              v-model="pressrelease.button_text"
              :element="pressrelease.button_text"
              @input="pressrelease.button_text = $event"
            >
            </BaseInput>
          </ValidationProvider>
          <ValidationProvider
            tag="div"
            class="form__row_col form__row_col--side"
            :rules="{ max: 255 }"
            v-slot="{ errors }"
          >
            <BaseInput
              placeholder="Button URL"
              :validateError="errors[0]"
              v-model="pressrelease.button_url"
              :element="pressrelease.button_url"
              @input="pressrelease.button_url = $event"
            >
            </BaseInput>
          </ValidationProvider>
        </div>
      </section>
      <section class="form">
        <h3 class="form__header">Images</h3>
        <div class="form__row">
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label">Search Image</span>
            <DragAndDropImage
              class="drag-and-drop--full-width"
              id="search"
              size="15"
              :image="pressrelease.search_image"
              :v-model="pressrelease.search_image"
              @input="
                pressrelease.search_image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                pressrelease.search_image = '';
              "
            ></DragAndDropImage>
          </div>
          <div class="form__row_col form__row_col--side form__row_col--bordered">
            <span class="form__row_label"
              >Header Image <span class="form__row_label_required">*</span></span
            >
            <DragAndDropImage
              class="drag-and-drop--full-width"
              id="header"
              size="15"
              :image="pressrelease.header_image"
              :v-model="pressrelease.header_image"
              @input="
                pressrelease.header_image = $event.image;
                $event.removed ? removedImages.push($event.removed) : false;
              "
              @removeImage="
                removedImages.push($event);
                pressrelease.header_image = '';
              "
            ></DragAndDropImage>
          </div>
        </div>
      </section>
    </ValidationObserver>
  </PopUp>
</template>
<script>
import PopUp from "@/components/PopUp.vue";
import BaseButton from "@/components/buttons/BaseButton.vue";
import BaseSelect from "@/components/inputs/BaseSelect.vue";
import BaseInput from "@/components/inputs/BaseInput.vue";
import BaseNumberInput from "@/components/inputs/BaseNumberInput.vue";
import Editor from "@/components/inputs/Editor.vue";
import DragAndDropImage from "@/components/DragAndDropImage.vue";
import DatePicker from "@/components/inputs/DatePicker.vue";
import MultipleAutocompleteInput from "@/components/inputs/MultipleAutocompleteInput.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";

import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  props: { item: { type: Object, default: null }, modal: { type: Boolean, default: false } },
  components: {
    PopUp,
    BaseButton,
    BaseSelect,
    BaseInput,
    BaseNumberInput,
    Editor,
    DatePicker,
    DragAndDropImage,
    MultipleAutocompleteInput,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      url: process.env.VUE_APP_FRONTEND_URL,
      today: null,
      pressrelease: {
        title: "",
        author: "",
        date_published: null,
        status: "0",
        slug: "",
        details: "",
        button_text: "",
        button_url: "",
        search_image: "",
        header_image: "",
        tags: [],
      },
      changed: false,
      removedImages: [],
    };
  },
  created() {
    this.today = this.$moment().tz("America/New_York").format("YYYY-MM-DD HH:mm");
    if (this.item != null) {
      this.pressrelease = JSON.parse(JSON.stringify(this.item));
    }
  },
  beforeDestroy() {
    this.setError(null);
  },
  computed: {
    ...mapGetters("pressreleases", {
      serverError: "getServerError",
      changesLoading: "getChangesLoading",
    }),
    ...mapGetters("tags", {
      matches: "getMatchedTags",
      tagsLoading: "getChangesLoading",
    }),
  },
  watch: {
    pressrelease: {
      handler: function (val, oldVal) {
        if (this.item != null) {
          if (this.$refs.observer.flags.dirty) {
            this.changed = true;
          }
        } else {
          this.changed = true;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("tags", ["apiPostTag", "apiGetMatchedTags"]),
    ...mapMutations("events", ["setError"]),
    hideModal() {
      if (this.changed) {
        this.$swal({
          html: "<h2>Are you sure you want to exit <br/> without saving?</h2>",
          showCancelButton: true,
          cancelButtonText: "Exit without saving",
          confirmButtonText: "Save changes",
        }).then(result => {
          if (result.value) {
            this.$refs.observer.flags.valid && this.pressrelease.header_image != ""
              ? this.savePressRelease()
              : this.showToast();
          } else this.$emit("hideModal");
        });
      } else this.$emit("hideModal");
    },
    showToast() {
      this.$toasted.show(`Please fill in all required fields`, {
        duration: 2000,
        type: "error",
      });
    },
    async addTag(val) {
      let data = { title: val };
      let result = await this.apiPostTag(data);
      if (result.success) {
        this.pressrelease.tags.push(result.tag);
        this.$emit("updateTag");
      } else {
        this.$toasted.show(result.error, {
          duration: 2000,
          type: "error",
        });
      }
    },
    savePressRelease() {
      let data = {
        ...this.pressrelease,
        tags: this.pressrelease.tags.map(el => {
          return el.id.toString();
        }),
      };
      if (this.pressrelease.id) {
        this.$emit("updateItem", { data });
      } else {
        this.$emit("createItem", { data });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.pressrelease {
  height: 100%;
  padding: 0 15px;
  overflow-y: auto;
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4rem;
    @media only screen and (max-width: $xs) {
      flex-direction: column;
      align-items: flex-start;
    }
    &_title {
      font-size: 3.35rem;
      font-weight: 600;
      line-height: 1.2;
      text-transform: capitalize;
      color: $black;
    }
    &_actions {
      flex: 1;
      display: flex;
      justify-content: flex-end;
      @media only screen and (max-width: $xs) {
        justify-content: flex-start;
        width: 100%;
      }
      .button {
        margin-left: 10px;
        @media only screen and (max-width: $xs) {
          margin: 20px 10px 0 0;
        }
      }
    }
  }
}
</style>
